import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faUsers, faCoins, faExclamationTriangle, faBuilding } from '@fortawesome/free-solid-svg-icons';
import {
  getAnalyticsOverview,
  getAnalyticsFunnel,
  getAnalyticsFunnels,
  getAnalyticsEvents,
  getAnalyticsCompany,
} from '../../services/api';
import './AnalyticsPage.scss';

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('ko-KR');
const formatPct = (value) => `${Math.round(Number(value || 0) * 100)}%`;

function AnalyticsPage() {
  const navigate = useNavigate();
  const today = useMemo(() => formatDateInputValue(new Date()), []);
  const [date, setDate] = useState(today);
  const [accountType, setAccountType] = useState('');
  const [overview, setOverview] = useState(null);
  const [funnels, setFunnels] = useState([]);
  const [funnelId, setFunnelId] = useState('persona_test');
  const [windowDays, setWindowDays] = useState(1);
  const [funnel, setFunnel] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, funnelCatalog, funnelData, eventsData] = await Promise.all([
        getAnalyticsOverview({ date, account_type: accountType || undefined }),
        getAnalyticsFunnels().catch(() => ({ funnels: [] })),
        getAnalyticsFunnel(funnelId, {
          cohort_date: date,
          window_days: windowDays,
          account_type: accountType || undefined,
          company_id: selectedCompanyId || undefined,
        }).catch(() => null),
        getAnalyticsEvents({
          start_date: date,
          end_date: date,
          limit: 40,
          company_id: selectedCompanyId || undefined,
        }).catch(() => ({ events: [] })),
      ]);
      setOverview(overviewData);
      setFunnels(funnelCatalog.funnels || []);
      setFunnel(funnelData);
      setEvents(eventsData.events || []);
    } catch (err) {
      setError(err.message || '분석 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [date, accountType, funnelId, windowDays, selectedCompanyId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setCompanyDetail(null);
      return;
    }
    let cancelled = false;
    setCompanyLoading(true);
    getAnalyticsCompany(selectedCompanyId, { date })
      .then((data) => {
        if (!cancelled) setCompanyDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || '회사 분석 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setCompanyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, date]);

  const visits = overview?.visits || {};
  const tokens = overview?.tokens || {};
  const health = overview?.health || {};

  return (
    <div className="sa-analytics">
      <div className="sa-page-header">
        <h1 className="sa-page-title">운영 애널리틱스</h1>
        <p className="sa-page-subtitle">일간 방문 · 토큰 · 기능 사용 · 퍼널 · 회사 drill-down</p>
      </div>

      <div className="sa-table-toolbar">
        <div className="sa-table-toolbar-left">
          <input
            type="date"
            className="sa-search-input"
            style={{ width: 160 }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <select
            className="sa-filter-select"
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
          >
            <option value="">전체 계정</option>
            <option value="business">기업</option>
            <option value="individual">개인</option>
          </select>
          <select
            className="sa-filter-select"
            value={funnelId}
            onChange={(e) => setFunnelId(e.target.value)}
          >
            {(funnels.length ? funnels : [
              { id: 'persona_test', name: 'Persona Test' },
              { id: 'activation', name: 'Activation' },
              { id: 'research_study', name: 'Research Study' },
            ]).map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
          <select
            className="sa-filter-select"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
          >
            <option value={1}>당일 퍼널</option>
            <option value={7}>7일 윈도우</option>
            <option value={14}>14일 윈도우</option>
          </select>
          {selectedCompanyId && (
            <button
              type="button"
              className="sa-btn sa-btn-secondary"
              onClick={() => setSelectedCompanyId(null)}
            >
              회사 필터 해제
            </button>
          )}
        </div>
        <div className="sa-table-toolbar-right">
          <button type="button" className="sa-btn sa-btn-primary" onClick={load}>
            새로고침
          </button>
        </div>
      </div>

      {error && <div className="sa-analytics-error">{error}</div>}

      <div className="sa-stats-grid">
        <div className="sa-stat-card">
          <div className="sa-stat-label"><FontAwesomeIcon icon={faUsers} /> DAU</div>
          <div className="sa-stat-value accent">{loading ? '...' : formatNumber(visits.dau)}</div>
          <div className="sa-stat-meta">세션 {formatNumber(visits.sessions)} · PV {formatNumber(visits.pageviews)}</div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-label"><FontAwesomeIcon icon={faCoins} /> 토큰</div>
          <div className="sa-stat-value accent">{loading ? '...' : formatNumber(tokens.billable_weighted_tokens)}</div>
          <div className="sa-stat-meta">요청 {formatNumber(tokens.request_count)} · ${Number(tokens.estimated_cost_usd || 0).toFixed(2)}</div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-label"><FontAwesomeIcon icon={faChartLine} /> 액션</div>
          <div className="sa-stat-value">{loading ? '...' : formatNumber(visits.actions)}</div>
          <div className="sa-stat-meta">핵심 제품 행동 수</div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-label"><FontAwesomeIcon icon={faExclamationTriangle} /> 실패/차단</div>
          <div className="sa-stat-value">{loading ? '...' : formatNumber(health.failures)}</div>
          <div className="sa-stat-meta">쿼터 차단 {formatNumber(health.quota_denied)}</div>
        </div>
      </div>

      <div className="sa-analytics-grid">
        <section className="sa-table-container sa-analytics-panel">
          <h3><FontAwesomeIcon icon={faBuilding} /> Top Companies (이벤트)</h3>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Users</th>
                <th>Events</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(overview?.top_companies || []).map((row) => (
                <tr key={row.company_id} className={selectedCompanyId === row.company_id ? 'is-selected' : ''}>
                  <td>{row.name}</td>
                  <td>{formatNumber(row.users)}</td>
                  <td>{formatNumber(row.events)}</td>
                  <td>
                    <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setSelectedCompanyId(row.company_id)}>
                      보기
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && !(overview?.top_companies || []).length && (
                <tr><td colSpan={4}>데이터 없음</td></tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="sa-table-container sa-analytics-panel">
          <h3>Top Companies (토큰)</h3>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Users</th>
                <th>Tokens</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(overview?.top_companies_by_tokens || []).map((row) => (
                <tr key={`tok-${row.company_id}`}>
                  <td>{row.name}</td>
                  <td>{formatNumber(row.users)}</td>
                  <td>{formatNumber(row.billable_weighted_tokens)}</td>
                  <td>
                    <button
                      type="button"
                      className="sa-btn sa-btn-secondary"
                      onClick={() => navigate(`/super-admin/companies/${row.company_id}/usage`)}
                    >
                      Usage
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && !(overview?.top_companies_by_tokens || []).length && (
                <tr><td colSpan={4}>데이터 없음</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      {selectedCompanyId && (
        <section className="sa-table-container sa-analytics-panel sa-company-detail">
          <h3>
            회사 Drill-down · {companyDetail?.company?.name || selectedCompanyId}
            {companyLoading ? ' (로딩...)' : ''}
          </h3>
          <div className="sa-company-kpis">
            <div>DAU {formatNumber(companyDetail?.visits?.dau)}</div>
            <div>Actions {formatNumber(companyDetail?.visits?.actions)}</div>
            <div>Failures {formatNumber(companyDetail?.visits?.failures)}</div>
            <div>Tokens {formatNumber(companyDetail?.tokens?.billable_weighted_tokens)}</div>
          </div>
          <div className="sa-analytics-grid">
            <div>
              <h4>유저별</h4>
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Events</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(companyDetail?.by_user || []).map((row) => (
                    <tr key={row.user_id}>
                      <td>{row.email || row.name || row.user_id}</td>
                      <td>{formatNumber(row.events)}</td>
                      <td>{formatNumber(row.actions)}</td>
                    </tr>
                  ))}
                  {!companyLoading && !(companyDetail?.by_user || []).length && (
                    <tr><td colSpan={3}>데이터 없음</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h4>기능별</h4>
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Users</th>
                    <th>Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {(companyDetail?.by_feature || []).map((row) => (
                    <tr key={row.feature_key}>
                      <td>{row.feature_key}</td>
                      <td>{formatNumber(row.users)}</td>
                      <td>{formatNumber(row.billable_weighted_tokens)}</td>
                    </tr>
                  ))}
                  {!companyLoading && !(companyDetail?.by_feature || []).length && (
                    <tr><td colSpan={3}>데이터 없음</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <div className="sa-analytics-grid">
        <section className="sa-table-container sa-analytics-panel">
          <h3>기능별 LLM 사용</h3>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Users</th>
                <th>Requests</th>
                <th>Tokens</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.by_feature || []).length === 0 && (
                <tr><td colSpan={4}>{loading ? '로딩 중...' : '데이터 없음'}</td></tr>
              )}
              {(overview?.by_feature || []).map((row) => (
                <tr key={row.feature_key}>
                  <td>{row.feature_key}</td>
                  <td>{formatNumber(row.users)}</td>
                  <td>{formatNumber(row.request_count)}</td>
                  <td>{formatNumber(row.billable_weighted_tokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="sa-table-container sa-analytics-panel">
          <h3>퍼널 · {funnel?.name || funnelId}</h3>
          <p className="sa-analytics-funnel-meta">
            코호트 {funnel?.cohort_date || date} · {funnel?.window_days || windowDays}일 · 시작 유저 {formatNumber(funnel?.cohort_size)}
            {selectedCompanyId ? ` · company ${selectedCompanyId}` : ''}
          </p>
          <div className="sa-funnel-steps">
            {(funnel?.steps || []).map((step) => (
              <div key={step.step_id} className="sa-funnel-step">
                <div className="sa-funnel-step-head">
                  <span>{step.step_id} {step.label}</span>
                  <strong>{formatNumber(step.users)} · {formatPct(step.rate_from_start)}</strong>
                </div>
                <div className="sa-funnel-bar">
                  <div className="sa-funnel-bar-fill" style={{ width: `${Math.max(2, Math.round((step.rate_from_start || 0) * 100))}%` }} />
                </div>
                {step.drop_from_prev > 0 && (
                  <div className="sa-funnel-drop">이전 대비 이탈 {formatPct(step.drop_from_prev)}</div>
                )}
              </div>
            ))}
            {!loading && !(funnel?.steps || []).length && <div className="sa-empty">퍼널 데이터 없음</div>}
          </div>
        </section>
      </div>

      <div className="sa-analytics-grid">
        <section className="sa-table-container sa-analytics-panel">
          <h3>Top Actions</h3>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Users</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.top_actions || []).map((row) => (
                <tr key={row.event_name}>
                  <td>{row.event_name}</td>
                  <td>{formatNumber(row.users)}</td>
                  <td>{formatNumber(row.count)}</td>
                </tr>
              ))}
              {!loading && !(overview?.top_actions || []).length && (
                <tr><td colSpan={3}>데이터 없음</td></tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="sa-table-container sa-analytics-panel">
          <h3>Top Paths</h3>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Path</th>
                <th>Users</th>
                <th>Views</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.top_paths || []).map((row) => (
                <tr key={row.path}>
                  <td>{row.path}</td>
                  <td>{formatNumber(row.users)}</td>
                  <td>{formatNumber(row.count)}</td>
                </tr>
              ))}
              {!loading && !(overview?.top_paths || []).length && (
                <tr><td colSpan={3}>데이터 없음</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      <section className="sa-table-container sa-analytics-panel">
        <h3>최근 이벤트</h3>
        <table className="sa-table">
          <thead>
            <tr>
              <th>시간</th>
              <th>Event</th>
              <th>User</th>
              <th>Path / Resource</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {events.map((row) => (
              <tr key={row.id}>
                <td>{row.occurred_at ? new Date(row.occurred_at).toLocaleString('ko-KR') : '-'}</td>
                <td>{row.event_name}</td>
                <td>{row.user_email || row.user_id || '-'}</td>
                <td>{row.path || (row.resource_type ? `${row.resource_type}:${row.resource_id}` : '-')}</td>
                <td>{row.status || '-'}</td>
              </tr>
            ))}
            {!loading && events.length === 0 && (
              <tr><td colSpan={5}>이벤트 없음 — 페이지 이동/로그인이 쌓이면 여기에 표시됩니다.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

export default AnalyticsPage;
